import { useRef, useEffect, useCallback } from 'react';

const usePoseVisualization = (webcamRef, poseLandmarkerRef, canvasRef, isFullBodyVisible, poseType, initialCountdownActive, isActive = true) => {
  const animationFrameRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);
  const landmarkHistoryRef = useRef([]);

  const exponentialSmoothLandmarks = (newLandmarks, history, baseAlpha = 0.6) => {
    if (!newLandmarks || newLandmarks.length === 0) return newLandmarks;
    if (history.length === 0) {
      history.push(newLandmarks);
      return newLandmarks;
    }

    const prevLandmarks = history[history.length - 1];
    const movementSpeed =
      newLandmarks.reduce((sum, landmark, i) => {
        const prev = prevLandmarks[i] || { x: 0, y: 0 };
        const dx = (landmark.x || 0) - prev.x;
        const dy = (landmark.y || 0) - prev.y;
        return sum + Math.sqrt(dx * dx + dy * dy);
      }, 0) / newLandmarks.length;

    const dynamicAlpha = Math.min(0.8, baseAlpha + movementSpeed * 5);

    const smoothed = newLandmarks.map((landmark, index) => {
      const prev = prevLandmarks[index] || { x: 0, y: 0, z: 0, visibility: 0 };
      const visibility = landmark.visibility || 0;
      const alpha = visibility < 0.2 ? dynamicAlpha * 0.6 : dynamicAlpha;
      return {
        x: alpha * (landmark.x || 0) + (1 - alpha) * prev.x,
        y: alpha * (landmark.y || 0) + (1 - alpha) * prev.y,
        z: alpha * (landmark.z || 0) + (1 - alpha) * prev.z,
        visibility: alpha * visibility + (1 - alpha) * prev.visibility
      };
    });

    history.push(smoothed);
    if (history.length > 2) history.shift();
    return smoothed;
  };

  const drawPose = useCallback(
    (landmarks) => {
      if (!canvasRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d', { alpha: true });
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!landmarks || landmarks.length === 0) return;

      ctx.lineWidth = 7;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.imageSmoothingEnabled = false;

      const visibilityThreshold = ['left_side', 'right_side', 'thread_needle', 'crow'].includes(poseType) ? 0.05 : 0.1;

      const connections = {
        left: [
          [11, 13],
          [13, 15],
          [15, 17],
          [15, 19],
          [15, 21],
          [17, 19],
          [19, 21],
          [23, 25],
          [25, 27],
          [27, 29],
          [29, 31],
          [11, 23]
        ],
        right: [
          [12, 14],
          [14, 16],
          [16, 18],
          [16, 20],
          [16, 22],
          [18, 20],
          [20, 22],
          [24, 26],
          [26, 28],
          [28, 30],
          [30, 32],
          [12, 24]
        ],
        center: [
          [0, 1],
          [0, 4],
          [1, 2],
          [2, 3],
          [4, 5],
          [5, 6],
          [0, 7],
          [0, 8],
          [9, 10],
          [11, 12],
          [23, 24]
        ]
      };

      const validConnections = [];
      if (!poseType.includes('right_side')) validConnections.push(...connections.left);
      if (!poseType.includes('left_side')) validConnections.push(...connections.right);
      validConnections.push(...connections.center);

      const filteredConnections = validConnections.filter(([i, j]) => {
        const p1 = landmarks[i];
        const p2 = landmarks[j];
        return p1 && p2 && p1.visibility >= visibilityThreshold && p2.visibility >= visibilityThreshold;
      });

      // During initial countdown, only show green lines if full body is visible
      // After countdown, use normal behavior
      const lineColor = initialCountdownActive
        ? isFullBodyVisible
          ? '#4caf50'
          : 'transparent'
        : isFullBodyVisible
          ? '#4caf50'
          : '#ffffff';

      console.log('Drawing pose lines:', {
        lineColor,
        isFullBodyVisible,
        poseType,
        initialCountdownActive,
        timestamp: new Date().toISOString(),
        visibleConnections: filteredConnections.length
      });

      ctx.strokeStyle = lineColor;
      ctx.beginPath();
      filteredConnections.forEach(([i, j]) => {
        const p1 = landmarks[i];
        const p2 = landmarks[j];
        ctx.moveTo(p1.x * canvas.width, p1.y * canvas.height);
        ctx.lineTo(p2.x * canvas.width, p2.y * canvas.height);
      });
      ctx.stroke();

      // Only draw points after countdown or if full body is visible during countdown
      if (!initialCountdownActive || isFullBodyVisible) {
        landmarks.forEach((point, index) => {
          if (!point || point.visibility < visibilityThreshold) return;
          const x = point.x * canvas.width;
          const y = point.y * canvas.height;

          ctx.beginPath();
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.arc(x, y, 8, 0, Math.PI * 2);
          ctx.fill();

          if (point.visibility > 0.6) {
            ctx.beginPath();
            ctx.fillStyle = 'rgba(76, 175, 80, 0.4)';
            ctx.arc(x, y, 12, 0, Math.PI * 2);
            ctx.fill();
          }

          ctx.beginPath();
          ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fill();
        });
      }
    },
    [canvasRef, isFullBodyVisible, poseType, initialCountdownActive]
  );

  const detectPose = useCallback(async () => {
    if (!isActive) {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
      return;
    }

    const video = webcamRef.current?.video;
    if (!video || video.readyState < 3 || !poseLandmarkerRef.current || !canvasRef.current) {
      console.log('Skipping pose detection:', {
        hasVideo: !!video,
        videoReadyState: video?.readyState,
        hasPoseLandmarker: !!poseLandmarkerRef.current,
        hasCanvas: !!canvasRef.current
      });
      animationFrameRef.current = requestAnimationFrame(detectPose);
      return;
    }

    if (lastVideoTimeRef.current !== video.currentTime) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const now = performance.now();
        const results = await poseLandmarkerRef.current.detectForVideo(video, now);
        if (results.landmarks?.length > 0) {
          const alpha = ['left_side', 'right_side'].includes(poseType) ? 0.5 : 0.6;
          const smoothedLandmarks = exponentialSmoothLandmarks(results.landmarks[0], landmarkHistoryRef.current, alpha);
          drawPose(smoothedLandmarks);
        } else {
          drawPose([]);
        }
      } catch (err) {
        console.error('Error detecting pose:', err);
        drawPose([]);
      }
    }

    if (isActive) {
      animationFrameRef.current = requestAnimationFrame(detectPose);
    }
  }, [webcamRef, poseLandmarkerRef, canvasRef, drawPose, poseType, isActive]);

  useEffect(() => {
    const video = webcamRef.current?.video;
    if (video && canvasRef.current) {
      canvasRef.current.width = video.videoWidth;
      canvasRef.current.height = video.videoHeight;
      console.log('Canvas dimensions set:', { width: video.videoWidth, height: video.videoHeight });
    }
    
    if (isActive) {
      animationFrameRef.current = requestAnimationFrame(detectPose);
    } else if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    
    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    };
  }, [detectPose, webcamRef, canvasRef]);

  return { drawPose };
};

export default usePoseVisualization;
